import CircleIcon from '@mui/icons-material/Circle';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import { Box, IconButton, Typography, useTheme } from '@mui/material';
import React, { useCallback, useMemo } from 'react';
import { Rnd } from 'react-rnd';
import { Trailer } from './trailer';
import { useTrailerStore } from './useTrailerStore';

interface Props {
  trailer: Trailer;
  onTrailerUpdate: (id: string) => void;
}

function TrailerUnitComponent({ trailer, onTrailerUpdate }: Props) {
  const theme = useTheme();
  const { updateTrailer } = useTrailerStore();

  const vertical = trailer.orientation === 'vertical';
  const isAvailable = trailer.details.status === 'available';

  const handleMarkUpdated = useCallback(() => {
    onTrailerUpdate(trailer.id);
  }, [onTrailerUpdate, trailer.id]);

  const onDragStop = useCallback(
    (_e: any, d: any) => {
      updateTrailer(trailer.id, { x: d.x, y: d.y });
      handleMarkUpdated();
    },
    [handleMarkUpdated, trailer.id, updateTrailer]
  );

  const onResizeStop = useCallback(
    (_e: any, _direction: any, ref: any, _delta: any, position: any) => {
      updateTrailer(trailer.id, {
        width: parseInt(ref.style.width, 10),
        height: parseInt(ref.style.height, 10),
        x: position.x,
        y: position.y,
      });
      handleMarkUpdated();
    },
    [handleMarkUpdated, trailer.id, updateTrailer]
  );

  const handleFlip = useCallback(() => {
    updateTrailer(trailer.id, {
      orientation: vertical ? 'horizontal' : 'vertical',
      width: trailer.height,
      height: trailer.width,
    });
    handleMarkUpdated();
  }, [handleMarkUpdated, trailer.height, trailer.id, trailer.width, updateTrailer, vertical]);

  const trailerName = trailer?.details?.name || 'N/A';
  const statusColor = isAvailable ? theme.palette.success.main : theme.palette.primary.main;
  const shellColor = isAvailable ? '#1f7a1f' : theme.palette.grey[100];

  const labelStyles = useMemo(
    () => ({
      transform: vertical ? 'none' : 'rotate(-90deg)',
      whiteSpace: 'nowrap' as const,
      textAlign: 'center' as const,
      fontWeight: 700,
      letterSpacing: 0.4,
      fontSize: vertical ? 15 : 14,
      lineHeight: 1.05,
      pointerEvents: 'none' as const,
      px: 1,
    }),
    [vertical]
  );

  return (
    <Rnd
      size={{ width: trailer.width, height: trailer.height }}
      position={{ x: trailer.x, y: trailer.y }}
      onDragStop={onDragStop}
      onResizeStop={onResizeStop}
      bounds="parent"
    >
      <Box
        sx={{
          width: '100%',
          height: '100%',
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 1.5,
          bgcolor: shellColor,
          color: '#fff',
          border: `2px solid ${statusColor}`,
          boxShadow: '0 4px 10px rgba(0,0,0,0.25)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          userSelect: 'none',
          transition: 'box-shadow 0.2s ease, transform 0.2s ease',
         
        }}
      >
          {/* <CircleIcon sx={{ fontSize: 10, color: statusColor }} /> */}

        <IconButton
          size="small"
          onClick={handleFlip}
          sx={{
            position: 'absolute',
            top: 2,
            right: 2,
            zIndex: 2,
            color: theme.palette.primary.dark,
            bgcolor: 'rgba(39, 38, 38, 0.08)',
            p: 0.35,
            '&:hover': {
              bgcolor: 'rgba(255,255,255,0.16)',
            },
          }}
        >
          <SyncAltIcon sx={{ fontSize: 16 }} />
        </IconButton>

        <Typography sx={labelStyles}>
          {trailerName}
        </Typography>
      </Box>
    </Rnd>
  );
}

const TrailerUnit = React.memo(TrailerUnitComponent);

export default TrailerUnit;